import {
    AirflowAssetEvent,
    AirflowAssetEventList,
    AirflowCalendarResponse,
    AirflowDag,
    AirflowDagDetails,
    AirflowDagList,
    AirflowDagRun,
    AirflowDagRunList,
    AirflowDagRunState,
    AirflowDagSource,
    AirflowImportError,
    AirflowImportErrorList,
    AirflowRunType,
    AirflowTagList,
    AirflowTaskDefinition,
    AirflowTaskDefinitionList,
    AirflowTaskInstance,
    AirflowTaskInstanceLinks,
    AirflowTaskInstanceList,
    AirflowTaskInstanceState,
    AirflowTaskLogsResponse,
    AirflowXcomEntry,
    AirflowXcomEntryList,
} from '@pages/airflow-beta/types';

/*
 * Standalone, in-memory fixture data + mock API implementation for the Airflow Dashboard,
 * used while the real backend gateway is still being built. Every method here mirrors the
 * real handler's signatures exactly (see airflow-dashboard-handler.real.ts) so swapping back
 * to the real API later is a one-line change in airflow-dashboard-handler.ts, and this whole
 * file can simply be deleted once the backend is live.
 */

const MOCK_NETWORK_DELAY_MS = 350;

/*
 * Simulates network latency so loading states are meaningfully testable against mock data
 */
function delay<T>(value: T): Promise<T> {
    return new Promise((resolve) => setTimeout(() => resolve(value), MOCK_NETWORK_DELAY_MS));
}

function randomInt(min: number, max: number) {
    return Math.floor(Math.random() * (max - min + 1)) + min;
}

function randomChoice<T>(items: T[]): T {
    return items[randomInt(0, items.length - 1)];
}

/*
 * Sorts by an `order_by`-style field string (optional leading "-" for descending)
 */
function sortByOrderBy<T>(items: T[], orderBy: string | undefined, getValue: (item: T, field: string) => any): T[] {
    if (!orderBy) return items;
    const desc = orderBy.startsWith('-');
    const field = desc ? orderBy.slice(1) : orderBy;
    const sorted = [...items].sort((a, b) => {
        const av = getValue(a, field);
        const bv = getValue(b, field);
        if (av === bv) return 0;
        return av > bv ? 1 : -1;
    });
    return desc ? sorted.reverse() : sorted;
}

function paginate<T>(items: T[], limit?: number, offset = 0): T[] {
    if (limit === undefined) return items.slice(offset);
    return items.slice(offset, offset + limit);
}

const NOW = Date.now();
const DAY_MS = 24 * 60 * 60 * 1000;

function isoAt(msAgo: number) {
    return new Date(NOW - msAgo).toISOString();
}

type DagSeed = {
    dagId: string;
    displayName?: string;
    tags: string[];
    owners: string[];
    description: string;
    timetableSummary: string;
    isPaused: boolean;
    hasImportErrors?: boolean;
    taskIds: string[];
};

const DAG_SEEDS: DagSeed[] = [
    {
        dagId: 'etl_daily_pipeline',
        tags: ['daily', 'etl'],
        owners: ['data-eng'],
        description: 'Extracts, transforms, and loads daily operational data into the warehouse.',
        timetableSummary: '0 2 * * *',
        isPaused: false,
        taskIds: ['extract_source', 'validate_schema', 'transform_records', 'load_warehouse', 'notify_success'],
    },
    {
        dagId: 'ml_training_pipeline',
        displayName: 'ML Training Pipeline',
        tags: ['ml', 'training'],
        owners: ['ml-team'],
        description: 'Retrains the demand-forecasting model on the latest feature set.',
        timetableSummary: '0 4 * * 1',
        isPaused: false,
        taskIds: ['fetch_features', 'train_model', 'evaluate_model', 'register_model'],
    },
    {
        dagId: 'data_quality_checks',
        tags: ['qa'],
        owners: ['data-eng'],
        description: 'Runs scheduled data quality assertions across core tables.',
        timetableSummary: '*/30 * * * *',
        isPaused: true,
        taskIds: ['check_nulls', 'check_duplicates', 'check_freshness'],
    },
    {
        dagId: 'model-request-a83f21',
        displayName: 'PI Model Request a83f21',
        tags: ['pi-integrator'],
        owners: ['pi-integrator'],
        description: 'Auto-generated request DAG managed by PI-integrator.',
        timetableSummary: '@once',
        isPaused: false,
        taskIds: ['fetch_pi_data', 'write_pi_output'],
    },
    {
        dagId: 'sensor_ingestion',
        tags: ['sensors', 'ingestion'],
        owners: ['iot-team'],
        description: 'Ingests raw sensor readings from edge nodes into the lake.',
        timetableSummary: '*/15 * * * *',
        isPaused: false,
        hasImportErrors: true,
        taskIds: ['poll_sensors', 'parse_readings', 'write_lake'],
    },
    {
        dagId: 'backfill_historical_load',
        tags: ['backfill'],
        owners: ['data-eng'],
        description: 'One-off historical backfill of pre-2024 well-log data.',
        timetableSummary: '@daily',
        isPaused: false,
        taskIds: ['scan_archive', 'convert_format', 'load_historical'],
    },
];

const OPERATORS = ['PythonOperator', 'BashOperator', 'SparkSubmitOperator', 'PostgresOperator'];
const TRIGGER_RULES = ['all_success', 'all_done', 'one_failed'];
const RUN_TYPES: AirflowRunType[] = ['scheduled', 'manual', 'backfill', 'asset_triggered'];
const USERS = ['pjay@deepiq.com', 'airflow-scheduler', 'jsmith@deepiq.com'];

function buildTasks(seed: DagSeed): AirflowTaskDefinition[] {
    return seed.taskIds.map((taskId, index) => ({
        task_id: taskId,
        task_display_name: taskId
            .split('_')
            .map((w) => w[0].toUpperCase() + w.slice(1))
            .join(' '),
        owner: seed.owners[0],
        trigger_rule: randomChoice(TRIGGER_RULES),
        retries: randomInt(0, 3),
        pool: 'default_pool',
        pool_slots: 1,
        priority_weight: 1,
        ui_color: `#${((index * 3517) % 0xffffff).toString(16).padStart(6, '0')}`,
        downstream_task_ids: index < seed.taskIds.length - 1 ? [seed.taskIds[index + 1]] : [],
        operator_name: randomChoice(OPERATORS),
        class_ref: { module_path: 'airflow.operators', class_name: randomChoice(OPERATORS) },
        is_mapped: index === 1,
    }));
}

function buildDagRuns(seed: DagSeed, count: number): AirflowDagRun[] {
    const runs: AirflowDagRun[] = [];
    for (let i = 0; i < count; i += 1) {
        const msAgo = i * (DAY_MS / 3) + randomInt(0, 3600_000);
        const durationSec = randomInt(30, 600);
        let state: AirflowDagRunState = 'success';
        if (i === 0) state = 'running';
        else if (i === 1) state = 'queued';
        else if (i % 6 === 0) state = 'failed';

        const runType = i % 5 === 0 ? 'manual' : randomChoice(RUN_TYPES);
        const startMs = NOW - msAgo;
        const endMs = state === 'running' || state === 'queued' ? undefined : startMs + durationSec * 1000;
        const runAfter = isoAt(msAgo);

        runs.push({
            dag_run_id: `${runType}__${runAfter}`,
            dag_id: seed.dagId,
            dag_display_name: seed.displayName,
            logical_date: runAfter,
            queued_at: runAfter,
            start_date: new Date(startMs).toISOString(),
            end_date: endMs ? new Date(endMs).toISOString() : undefined,
            duration: state === 'running' || state === 'queued' ? undefined : durationSec,
            data_interval_start: runAfter,
            data_interval_end: isoAt(msAgo - DAY_MS),
            run_after: runAfter,
            last_scheduling_decision: runAfter,
            state,
            run_type: runType,
            triggered_by: runType === 'manual' ? 'ui' : 'scheduler',
            triggering_user_name: runType === 'manual' ? randomChoice(USERS) : null,
            conf: {},
            note: null,
            dag_versions: [{ id: `${seed.dagId}-v1`, version_number: 1, dag_id: seed.dagId }],
        });
    }
    return runs;
}

function taskInstanceStateForRun(runState: AirflowDagRunState, taskIndex: number, taskCount: number, failureIndex: number): AirflowTaskInstanceState {
    if (runState === 'queued') return 'queued';
    if (runState === 'running') {
        if (taskIndex < failureIndex) return 'success';
        if (taskIndex === failureIndex) return 'running';
        return 'scheduled';
    }
    if (runState === 'failed') {
        if (taskIndex < failureIndex) return 'success';
        if (taskIndex === failureIndex) return 'failed';
        return 'upstream_failed';
    }
    return taskIndex === taskCount - 1 && Math.random() < 0.05 ? 'skipped' : 'success';
}

function buildTaskInstancesForRun(seed: DagSeed, tasks: AirflowTaskDefinition[], run: AirflowDagRun): AirflowTaskInstance[] {
    const failureIndex = randomInt(0, tasks.length - 1);
    const runStartMs = new Date(run.start_date ?? run.logical_date ?? isoAt(0)).getTime();

    return tasks.map((task, index) => {
        const state = taskInstanceStateForRun(run.state, index, tasks.length, failureIndex);
        const taskStartMs = runStartMs + index * 15_000;
        const isRunningOrQueued = state === 'running' || state === 'queued' || state === 'scheduled';
        const durationSec = isRunningOrQueued ? undefined : randomInt(5, 90);

        return {
            id: `${run.dag_run_id}::${task.task_id}`,
            task_id: task.task_id,
            task_display_name: task.task_display_name,
            dag_id: seed.dagId,
            dag_display_name: seed.displayName,
            dag_run_id: run.dag_run_id,
            run_id: run.dag_run_id,
            state,
            start_date: new Date(taskStartMs).toISOString(),
            end_date: durationSec ? new Date(taskStartMs + durationSec * 1000).toISOString() : undefined,
            duration: durationSec ?? null,
            try_number: state === 'failed' ? randomInt(1, 3) : 1,
            max_tries: task.retries ?? 0,
            map_index: task.is_mapped ? 0 : -1,
            operator: task.operator_name,
            operator_name: task.operator_name,
            queued_when: new Date(taskStartMs - 2000).toISOString(),
            scheduled_when: new Date(taskStartMs - 3000).toISOString(),
            logical_date: run.logical_date,
            run_after: run.run_after,
            pool: task.pool,
        };
    });
}

/*
 * All mock state, generated once per module load (dev-session lifetime)
 */
const mockDags: AirflowDag[] = [];
const mockDagDetails = new Map<string, AirflowDagDetails>();
const mockTasksByDag = new Map<string, AirflowTaskDefinition[]>();
const mockRunsByDag = new Map<string, AirflowDagRun[]>();
const mockTaskInstancesByRun = new Map<string, AirflowTaskInstance[]>();
const mockSourceByDag = new Map<string, string>();
const mockXcomByKey = new Map<string, AirflowXcomEntry[]>();
const mockAssetEvents: AirflowAssetEvent[] = [];

function xcomKey(dagId: string, dagRunId: string, taskId: string) {
    return `${dagId}::${dagRunId}::${taskId}`;
}

function runInstancesKey(dagId: string, dagRunId: string) {
    return `${dagId}::${dagRunId}`;
}

function buildDagSource(seed: DagSeed): string {
    return [
        'from airflow import DAG',
        'from airflow.operators.python import PythonOperator',
        'from datetime import datetime',
        '',
        `with DAG("${seed.dagId}", schedule="${seed.timetableSummary}", start_date=datetime(2024, 1, 1), catchup=False) as dag:`,
        ...seed.taskIds.map((taskId) => `    ${taskId} = PythonOperator(task_id="${taskId}", python_callable=lambda: None)`),
        '',
        `    ${seed.taskIds.join(' >> ')}`,
    ].join('\n');
}

DAG_SEEDS.forEach((seed) => {
    const tasks = buildTasks(seed);
    const runs = buildDagRuns(seed, 24);

    mockDags.push({
        dag_id: seed.dagId,
        dag_display_name: seed.displayName,
        is_paused: seed.isPaused,
        is_stale: false,
        last_parsed_time: isoAt(randomInt(0, 3600_000)),
        fileloc: `/opt/airflow/dags/${seed.dagId}.py`,
        file_token: seed.dagId,
        owners: seed.owners,
        description: seed.description,
        timetable_summary: seed.timetableSummary,
        tags: seed.tags.map((name) => ({ name })),
        max_active_tasks: 16,
        max_active_runs: 1,
        has_import_errors: seed.hasImportErrors ?? false,
        next_dagrun_logical_date: seed.isPaused ? undefined : isoAt(-3600_000),
        latest_dag_runs: runs.slice(0, 1).map((r) => ({ dag_run_id: r.dag_run_id, state: r.state, logical_date: r.logical_date })),
    });

    mockDagDetails.set(seed.dagId, {
        dag_id: seed.dagId,
        dag_display_name: seed.displayName,
        is_paused: seed.isPaused,
        owners: seed.owners,
        tags: seed.tags.map((name) => ({ name })),
        has_import_errors: seed.hasImportErrors ?? false,
        catchup: false,
        max_active_runs: 1,
        max_active_tasks: 16,
        start_date: '2024-01-01T00:00:00Z',
        timezone: 'UTC',
        last_parsed: isoAt(randomInt(0, 3600_000)),
        last_parse_duration: Math.random() * 2,
        fileloc: `/opt/airflow/dags/${seed.dagId}.py`,
        latest_dag_version: { id: `${seed.dagId}-v1`, version_number: 1, dag_id: seed.dagId, created_at: '2024-01-01T00:00:00Z' },
    });

    mockTasksByDag.set(seed.dagId, tasks);
    mockRunsByDag.set(seed.dagId, runs);
    runs.forEach((run) => {
        mockTaskInstancesByRun.set(runInstancesKey(seed.dagId, run.dag_run_id), buildTaskInstancesForRun(seed, tasks, run));
    });
    mockSourceByDag.set(seed.dagId, buildDagSource(seed));
});

const MOCK_IMPORT_ERRORS: AirflowImportError[] = DAG_SEEDS.filter((seed) => seed.hasImportErrors).map((seed, index) => ({
    import_error_id: index + 1,
    timestamp: isoAt(randomInt(0, 3600_000)),
    filename: `/opt/airflow/dags/${seed.dagId}.py`,
    stack_trace: [
        'Traceback (most recent call last):',
        `  File "/opt/airflow/dags/${seed.dagId}.py", line 8, in <module>`,
        '    from airflow.sensors.edge import EdgeSensor',
        "ModuleNotFoundError: No module named 'airflow.sensors.edge'",
    ].join('\n'),
}));

const PLAIN_TEXT_LOG_SAMPLE = [
    '[2024-06-01 02:00:01,123] {taskinstance.py:1103} INFO - Starting attempt 1 of 1',
    '[2024-06-01 02:00:01,456] {taskinstance.py:1122} INFO - Executing task on worker-3',
    '[2024-06-01 02:00:02,001] {standard_task_runner.py:57} INFO - Running: [\'airflow\', \'tasks\', \'run\']',
    'Traceback (most recent call last):',
    '  File "/opt/airflow/dags/etl_daily_pipeline.py", line 12, in extract_source',
    '    connect_to_source()',
    'ConnectionError: could not connect to source database',
    '[2024-06-01 02:00:05,890] {taskinstance.py:1345} INFO - Marking task as SUCCESS.',
].join('\n');

const NDJSON_LOG_SAMPLE = [
    JSON.stringify({ timestamp: '2024-06-01T02:00:01.123Z', level: 'info', logger: 'task', event: '::group::Setup' }),
    JSON.stringify({ timestamp: '2024-06-01T02:00:01.456Z', level: 'info', logger: 'task', event: 'Resolved connection pool' }),
    JSON.stringify({ timestamp: '2024-06-01T02:00:01.789Z', level: 'info', logger: 'task', event: '::endgroup::' }),
    JSON.stringify({ timestamp: '2024-06-01T02:00:02.001Z', level: 'info', logger: 'task', event: 'Processing 4,281 rows' }),
    JSON.stringify({ timestamp: '2024-06-01T02:00:04.321Z', level: 'warning', logger: 'task', event: 'Row 88 had a null value in optional column' }),
    JSON.stringify({ timestamp: '2024-06-01T02:00:05.890Z', level: 'info', logger: 'task', event: 'Task completed successfully' }),
].join('\n');

/*
 * Mock implementation of the Airflow Dashboard API client — same method signatures as the
 * real handler, operating over the in-memory fixtures above instead of the network.
 */
export const AirflowDashboardHandler = {
    async listDags(_Env: unknown, params: any = {}): Promise<AirflowDagList> {
        let filtered = mockDags;
        if (params.dagIdPattern) {
            const pattern = params.dagIdPattern.toLowerCase();
            filtered = filtered.filter((d) => d.dag_id.toLowerCase().includes(pattern));
        }
        if (params.paused !== undefined) filtered = filtered.filter((d) => d.is_paused === params.paused);
        if (params.tags?.length) {
            filtered = filtered.filter((d) => d.tags.some((t) => params.tags.includes(t.name)));
        }
        filtered = sortByOrderBy(filtered, params.orderBy, (item, field) => (item as any)[field]);
        return delay({ dags: paginate(filtered, params.limit, params.offset ?? 0), total_entries: filtered.length });
    },

    async getDag(_Env: unknown, dagId: string): Promise<AirflowDag> {
        const dag = mockDags.find((d) => d.dag_id === dagId);
        if (!dag) throw new Error(`Mock DAG "${dagId}" not found`);
        return delay(dag);
    },

    async getDagDetails(_Env: unknown, dagId: string): Promise<AirflowDagDetails> {
        const details = mockDagDetails.get(dagId);
        if (!details) throw new Error(`Mock DAG details "${dagId}" not found`);
        return delay(details);
    },

    async deleteDag(_Env: unknown, dagId: string): Promise<void> {
        const index = mockDags.findIndex((d) => d.dag_id === dagId);
        if (index >= 0) mockDags.splice(index, 1);
        return delay(undefined);
    },

    async patchDag(_Env: unknown, dagId: string, body: Partial<Pick<AirflowDag, 'is_paused'>>): Promise<AirflowDag> {
        const dag = mockDags.find((d) => d.dag_id === dagId);
        if (!dag) throw new Error(`Mock DAG "${dagId}" not found`);
        if (body.is_paused !== undefined) dag.is_paused = body.is_paused;
        return delay(dag);
    },

    async triggerDag(_Env: unknown, dagId: string, params: any = {}): Promise<AirflowDagRun> {
        const seed = DAG_SEEDS.find((s) => s.dagId === dagId);
        const runs = mockRunsByDag.get(dagId) ?? [];
        const tasks = mockTasksByDag.get(dagId) ?? [];
        const logicalDate = params.logical_date ?? new Date().toISOString();
        const newRun: AirflowDagRun = {
            dag_run_id: `manual__${logicalDate}`,
            dag_id: dagId,
            logical_date: logicalDate,
            queued_at: new Date().toISOString(),
            start_date: new Date().toISOString(),
            run_after: logicalDate,
            state: 'queued',
            run_type: 'manual',
            triggered_by: 'ui',
            triggering_user_name: 'you',
            conf: params.conf ?? {},
            note: null,
        };
        runs.unshift(newRun);
        if (seed) mockTaskInstancesByRun.set(runInstancesKey(dagId, newRun.dag_run_id), buildTaskInstancesForRun(seed, tasks, newRun));
        return delay(newRun);
    },

    async getDagTask(_Env: unknown, dagId: string, taskId: string): Promise<AirflowTaskDefinition> {
        const task = (mockTasksByDag.get(dagId) ?? []).find((t) => t.task_id === taskId);
        if (!task) throw new Error(`Mock task "${taskId}" not found`);
        return delay(task);
    },

    async listDagTasks(_Env: unknown, dagId: string): Promise<AirflowTaskDefinitionList> {
        const tasks = mockTasksByDag.get(dagId) ?? [];
        return delay({ tasks, total_entries: tasks.length });
    },

    async listDagTags(_Env: unknown, params: any = {}): Promise<AirflowTagList> {
        const tags = Array.from(new Set(mockDags.flatMap((d) => d.tags.map((t) => t.name)))).sort();
        return delay({ tags: paginate(tags, params.limit, params.offset ?? 0), total_entries: tags.length });
    },

    async getDagSource(_Env: unknown, fileToken: string): Promise<AirflowDagSource> {
        // Mock DAGs use their own dag_id as the file_token for simplicity
        return delay({ content: mockSourceByDag.get(fileToken) ?? '', dag_id: fileToken });
    },

    // Mock counterpart of the Code tab's Submit action (POST {gateway}/airflow/api/v1/dags/push)
    // — kept as a local-dev fallback like every other LIVE_METHODS-covered call.
    async pushDagSource(_Env: unknown, fileName: string, dagContent: string): Promise<AirflowDagSource> {
        mockSourceByDag.set(fileName, dagContent);
        return delay({ content: dagContent, dag_id: fileName });
    },

    async listDagRuns(_Env: unknown, dagId: string, params: any = {}): Promise<AirflowDagRunList> {
        let runs = mockRunsByDag.get(dagId) ?? [];
        if (params.state?.length) runs = runs.filter((r) => params.state.includes(r.state));
        if (params.runType?.length) runs = runs.filter((r) => params.runType.includes(r.run_type));
        if (params.runIdPattern) runs = runs.filter((r) => r.dag_run_id.includes(params.runIdPattern));
        if (params.triggeringUserNamePattern) {
            runs = runs.filter((r) => r.triggering_user_name?.includes(params.triggeringUserNamePattern));
        }
        if (params.runAfterGte) runs = runs.filter((r) => (r.run_after ?? '') >= params.runAfterGte);
        if (params.runAfterLte) runs = runs.filter((r) => (r.run_after ?? '') <= params.runAfterLte);
        if (params.logicalDateGte) runs = runs.filter((r) => (r.logical_date ?? '') >= params.logicalDateGte);
        if (params.logicalDateLte) runs = runs.filter((r) => (r.logical_date ?? '') <= params.logicalDateLte);
        runs = sortByOrderBy(runs, params.order_by, (item, field) => (item as any)[field] ?? '');
        return delay({ dag_runs: paginate(runs, params.limit, params.offset ?? 0), total_entries: runs.length });
    },

    async patchDagRun(_Env: unknown, dagId: string, dagRunId: string, body: { state?: string; note?: string | null }): Promise<AirflowDagRun> {
        const run = (mockRunsByDag.get(dagId) ?? []).find((r) => r.dag_run_id === dagRunId);
        if (!run) throw new Error(`Mock run "${dagRunId}" not found`);
        if (body.state) run.state = body.state as AirflowDagRunState;
        if (body.note !== undefined) run.note = body.note;
        return delay(run);
    },

    async deleteDagRun(_Env: unknown, dagId: string, dagRunId: string): Promise<void> {
        const runs = mockRunsByDag.get(dagId) ?? [];
        const index = runs.findIndex((r) => r.dag_run_id === dagRunId);
        if (index >= 0) runs.splice(index, 1);
        mockTaskInstancesByRun.delete(runInstancesKey(dagId, dagRunId));
        return delay(undefined);
    },

    async getDagRun(_Env: unknown, dagId: string, dagRunId: string): Promise<AirflowDagRun> {
        const run = (mockRunsByDag.get(dagId) ?? []).find((r) => r.dag_run_id === dagRunId);
        if (!run) throw new Error(`Mock run "${dagRunId}" not found`);
        return delay(run);
    },

    async getDagCalendar(_Env: unknown, dagId: string, params: { logicalDateGte?: string; logicalDateLte?: string }): Promise<AirflowCalendarResponse> {
        const runs = (mockRunsByDag.get(dagId) ?? []).filter((r) => {
            const date = r.logical_date ?? '';
            return (!params.logicalDateGte || date >= params.logicalDateGte) && (!params.logicalDateLte || date <= params.logicalDateLte);
        });
        const entries = runs.map((r) => ({ count: 1, date: r.logical_date ?? '', state: r.state }));
        return delay({ dag_runs: entries, total_entries: entries.length });
    },

    async clearDagRun(_Env: unknown, dagId: string, dagRunId: string, payload: { onlyFailed?: boolean }): Promise<{ task_instances: AirflowTaskInstance[] }> {
        const instances = mockTaskInstancesByRun.get(runInstancesKey(dagId, dagRunId)) ?? [];
        const affected = payload.onlyFailed ? instances.filter((ti) => ti.state === 'failed' || ti.state === 'upstream_failed') : instances;
        return delay({ task_instances: affected });
    },

    async listDagTaskInstances(_Env: unknown, dagId: string, params: any = {}): Promise<AirflowTaskInstanceList> {
        let instances = (mockRunsByDag.get(dagId) ?? []).flatMap((run) => mockTaskInstancesByRun.get(runInstancesKey(dagId, run.dag_run_id)) ?? []);
        if (params.state?.length) instances = instances.filter((ti) => ti.state && params.state.includes(ti.state));
        if (params.taskId) instances = instances.filter((ti) => ti.task_id === params.taskId);
        if (params.runAfterGte) instances = instances.filter((ti) => (ti.run_after ?? '') >= params.runAfterGte);
        if (params.runAfterLte) instances = instances.filter((ti) => (ti.run_after ?? '') <= params.runAfterLte);
        instances = sortByOrderBy(instances, params.order_by, (item, field) => (item as any)[field] ?? '');
        return delay({ task_instances: paginate(instances, params.limit, params.offset ?? 0), total_entries: instances.length });
    },

    async listRunTaskInstances(_Env: unknown, dagId: string, dagRunId: string, params: any = {}): Promise<AirflowTaskInstanceList> {
        let instances = mockTaskInstancesByRun.get(runInstancesKey(dagId, dagRunId)) ?? [];
        if (params.state?.length) instances = instances.filter((ti) => ti.state && params.state.includes(ti.state));
        instances = sortByOrderBy(instances, params.order_by, (item, field) => (item as any)[field] ?? '');
        return delay({ task_instances: paginate(instances, params.limit, params.offset ?? 0), total_entries: instances.length });
    },

    async clearTaskInstances(_Env: unknown, dagId: string, payload: { dagRunId: string; taskIds: string[]; onlyFailed?: boolean }): Promise<{ task_instances: AirflowTaskInstance[] }> {
        const instances = mockTaskInstancesByRun.get(runInstancesKey(dagId, payload.dagRunId)) ?? [];
        let affected = instances.filter((ti) => payload.taskIds.includes(ti.task_id));
        if (payload.onlyFailed) affected = affected.filter((ti) => ti.state === 'failed' || ti.state === 'upstream_failed');
        return delay({ task_instances: affected });
    },

    async getTaskLogs(_Env: unknown, _dagId: string, _dagRunId: string, taskId: string, tryNumber = 1): Promise<AirflowTaskLogsResponse> {
        const useNdjson = (taskId.length + tryNumber) % 2 === 0;
        return delay({ content: useNdjson ? NDJSON_LOG_SAMPLE : PLAIN_TEXT_LOG_SAMPLE, continuation_token: null });
    },

    async getRunTaskInstance(_Env: unknown, dagId: string, dagRunId: string, taskId: string): Promise<AirflowTaskInstance> {
        const instance = (mockTaskInstancesByRun.get(runInstancesKey(dagId, dagRunId)) ?? []).find((ti) => ti.task_id === taskId);
        if (!instance) throw new Error(`Mock task instance "${taskId}" not found`);
        return delay(instance);
    },

    async getTaskInstanceLinks(): Promise<AirflowTaskInstanceLinks> {
        return delay({ Log: 'https://logs.example.com/mock', Graph: null });
    },

    async patchTaskInstance(_Env: unknown, dagId: string, dagRunId: string, taskId: string, body: { new_state: string }): Promise<AirflowTaskInstance> {
        const instance = (mockTaskInstancesByRun.get(runInstancesKey(dagId, dagRunId)) ?? []).find((ti) => ti.task_id === taskId);
        if (!instance) throw new Error(`Mock task instance "${taskId}" not found`);
        instance.state = body.new_state as AirflowTaskInstanceState;
        return delay(instance);
    },

    async deleteTaskInstance(_Env: unknown, dagId: string, dagRunId: string, taskId: string): Promise<void> {
        const instances = mockTaskInstancesByRun.get(runInstancesKey(dagId, dagRunId)) ?? [];
        const index = instances.findIndex((ti) => ti.task_id === taskId);
        if (index >= 0) instances.splice(index, 1);
        return delay(undefined);
    },

    async listXcomEntries(_Env: unknown, dagId: string, dagRunId: string, taskId: string, params: any = {}): Promise<AirflowXcomEntryList> {
        const entries = mockXcomByKey.get(xcomKey(dagId, dagRunId, taskId)) ?? [];
        return delay({ xcom_entries: paginate(entries, params.limit ?? 50, params.offset ?? 0), total_entries: entries.length });
    },

    async createXcomEntry(_Env: unknown, dagId: string, dagRunId: string, taskId: string, body: { key: string; value: unknown; map_index?: number }): Promise<AirflowXcomEntry> {
        const entry: AirflowXcomEntry = {
            key: body.key,
            dag_id: dagId,
            run_id: dagRunId,
            task_id: taskId,
            map_index: body.map_index ?? -1,
            timestamp: new Date().toISOString(),
            value: body.value,
        };
        const key = xcomKey(dagId, dagRunId, taskId);
        mockXcomByKey.set(key, [entry, ...(mockXcomByKey.get(key) ?? [])]);
        return delay(entry);
    },

    async updateXcomEntry(_Env: unknown, dagId: string, dagRunId: string, taskId: string, xcomEntryKey: string, body: { value: unknown }): Promise<AirflowXcomEntry> {
        const entries = mockXcomByKey.get(xcomKey(dagId, dagRunId, taskId)) ?? [];
        const entry = entries.find((e) => e.key === xcomEntryKey);
        if (!entry) throw new Error(`Mock XCom entry "${xcomEntryKey}" not found`);
        entry.value = body.value;
        return delay(entry);
    },

    async deleteXcomEntry(_Env: unknown, dagId: string, dagRunId: string, taskId: string, xcomEntryKey: string): Promise<void> {
        const key = xcomKey(dagId, dagRunId, taskId);
        const entries = mockXcomByKey.get(key) ?? [];
        mockXcomByKey.set(key, entries.filter((e) => e.key !== xcomEntryKey));
        return delay(undefined);
    },

    async listImportErrors(_Env: unknown, params: any = {}): Promise<AirflowImportErrorList> {
        return delay({ import_errors: paginate(MOCK_IMPORT_ERRORS, params.limit, params.offset ?? 0), total_entries: MOCK_IMPORT_ERRORS.length });
    },

    async listAssetEvents(_Env: unknown, params: any): Promise<AirflowAssetEventList> {
        const events = mockAssetEvents.filter(
            (e) => e.source_dag_id === params.sourceDagId && e.source_task_id === params.sourceTaskId && e.source_run_id === params.sourceRunId
        );
        return delay({ asset_events: paginate(events, params.limit ?? 50, params.offset ?? 0), total_entries: events.length });
    },
};
